import { registerLocaleData } from '@angular/common';
import { provideHttpClient } from '@angular/common/http';
import localeFrCa from '@angular/common/locales/fr-CA';
import {
  ApplicationConfig,
  LOCALE_ID,
  provideZonelessChangeDetection
} from '@angular/core';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS } from '@angular/material/autocomplete';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/slide-toggle';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { provideRouter, withInMemoryScrolling } from '@angular/router';

import { provideAuthentification } from '@igo2/auth';
import { withMicrosoftSupport } from '@igo2/auth/microsoft';
import { provideAuthUserMonitoring } from '@igo2/auth/monitoring';
import { mspMsalServiceFactory } from '@igo2/auth/msp';
import { provideIcon } from '@igo2/common/icon';
import { provideTranslation, withStaticConfig } from '@igo2/core/language';
import { provideMessage } from '@igo2/core/message';
import { provideSentryMonitoring } from '@igo2/core/monitoring';
import { ICHERCHE_CONFIG, TERRAPI_CONFIG } from '@igo2/sdg-carto';
import { provideNavigationTitle } from '@igo2/sdg-common';

import { EnvironmentOptions, environment, isPartenaireEnv } from '@env';

import { routes } from './app.routes';

registerLocaleData(localeFrCa, 'fr-CA');

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideHttpClient(),
    provideTranslation(withStaticConfig({ ignoreLibsLocale: true }, 'fr')),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled'
      })
    ),
    provideMessage(),
    provideAppAuth(environment),
    provideNavigationTitle({
      separator: '|',
      suffix: 'Vigilance - Surveillance de la crue des eaux'
    }),
    provideIcon(),
    provideMonitoring(environment),
    { provide: LOCALE_ID, useValue: 'fr-CA' },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', subscriptSizing: 'dynamic' }
    },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { hideSingleSelectionIndicator: true }
    },
    {
      provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
      useValue: { hideSingleSelectionIndicator: true }
    },
    {
      provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS,
      useValue: { hideIcon: true }
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 500,
        hideDelay: 0,
        touchendHideDelay: 0,
        disableTooltipInteractivity: false
      }
    },
    {
      provide: ICHERCHE_CONFIG,
      useValue: {
        searchUrl: environment.search?.icherche?.searchUrl ?? '/apis/icherche'
      }
    },
    {
      provide: TERRAPI_CONFIG,
      useValue: {
        searchUrl: environment.search?.terrapi?.searchUrl ?? '/apis/terrapi'
      }
    }
  ]
};

// AuthService always injects AUTH_OPTIONS, even in the public build, so it must always be provided.
function provideAppAuth(environment: EnvironmentOptions) {
  const features = isPartenaireEnv(environment)
    ? [withMicrosoftSupport('default', mspMsalServiceFactory)]
    : [];

  return provideAuthentification(environment.auth, ...features);
}

function provideMonitoring(environment: EnvironmentOptions) {
  if (!environment.monitoring) {
    return [];
  }

  const features = provideSentryMonitoring(environment.monitoring);

  if (isPartenaireEnv(environment)) {
    features.push(...provideAuthUserMonitoring(environment.monitoring!));
  }

  return features;
}
